import { format as formatDate } from 'date-fns';

import { Importer, type Tasks } from '@air/api';
import { type Board, ClipSource, type ImportFileInfo, type ImportSourceNameShared } from '@air/api/types';
import { INVALID_IMPORT_SESSION, matchesAirror } from '@air/errors';
import { formatBytes } from '@air/utilities';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type ImporterTaskEnhanced } from '~/components/Import/utils';
import {
  type ImporterFileInfoType,
  type ImporterFilesResult,
  type ImporterFolderInfoType,
  type ImporterItemsInfoType,
  type ImportOptions,
} from '~/swr-hooks/importer/types';
import { type BasicFileInfoType } from '~/utils/ImportUtils';

const PAGE_SIZE = 15;

type ImportOAuthSourceName = Parameters<typeof Importer.createAuthUrl>[0]['source'];

export const isImportOAuthSource = (source: ImportSourceNameShared): source is ImportOAuthSourceName =>
  source === 'box' ||
  source === 'googleDrive' ||
  source === 'dropbox' ||
  source === 'sharePoint' ||
  source === 'frameio';

export const isImportWithoutOAuthSource = (source: ImportSourceNameShared) =>
  source === 'brandfolder' || source === 'canto';

export const getDefaultImportOptions = (source: ImportSourceNameShared): ImportOptions => {
  switch (source) {
    case ClipSource.dropbox:
      return { importTags: false };
    case ClipSource.sharePoint:
      return { importCustomFields: false, importVersions: false };
    case ClipSource.frameio:
      return { importCustomFields: false };
    case ClipSource.canto:
      return {
        importCustomFields: true,
        importTags: true,
        importKeywords: true,
        importVersions: false,
      };
    default:
      return {};
  }
};

const isImporterItemsInfo = (data: Record<string, unknown>): data is ImporterItemsInfoType =>
  'hash' in data && 'name' in data && 'files' in data && 'folders' in data;

export const getSourceAuthorizationUrl = async (source: ImportSourceNameShared, workspaceId: string) => {
  if (!isImportOAuthSource(source)) {
    reportErrorToBugsnag({
      error: new Error('Import source does not support OAuth authorization'),
      context: 'Can not create authorization url',
      metadata: {
        data: {
          source,
        },
      },
    });
    return '';
  }

  try {
    const { origin, pathname } = window.location;
    const cleanRedirectUrl = `${origin}${pathname}`;
    const { url } = await Importer.createAuthUrl({
      workspaceId,
      source,
      redirectUrl: cleanRedirectUrl,
    });

    return url;
  } catch (error) {
    reportErrorToBugsnag({
      error,
      context: 'Can not create authorization url',
      metadata: {
        data: {
          source,
        },
      },
    });
    return '';
  }
};

export const filesForImportFetcher = async ({
  parent,
  source,
  path,
  pageParam,
  workspaceId,
  targetAccountId,
}: {
  parent: BasicFileInfoType;
  source: ImportSourceNameShared;
  path: string;
  pageParam?: string;
  workspaceId: string;
  targetAccountId: string | undefined;
}): Promise<ImporterFilesResult> => {
  try {
    const result = await Importer.getFolder({
      source,
      descriptor: path,
      cursor: pageParam,
      pageSize: PAGE_SIZE,
      workspaceId,
      targetAccountId,
    });

    if (!isImporterItemsInfo(result)) {
      throw new Error('Unexpected response shape from Importer.getFolder');
    }

    const { hash: parentHash, files, folders, cursor } = result;

    const contents: ImportFileInfo[] = [];

    files
      .filter(
        ({ hash, descriptor, importedName, recordedAt }) => !!hash && !!importedName && !!recordedAt && !!descriptor,
      )
      .forEach(({ hash, importedName, size, recordedAt, thumbnail, descriptor, ext }: ImporterFileInfoType) => {
        contents.push({
          id: hash,
          downloadKey: descriptor,
          name: importedName,
          isFile: true,
          sizeDisplay: formatBytes(size, { numberOfDecimals: 2 }),
          dateDisplay: formatDate(new Date(recordedAt), 'MM/dd/yyyy'),
          thumbnail,
          parentHash: `${parent.parentHash || parentHash}/${hash}`,
          ext,
        });
      });

    folders
      .filter(({ hash, name, descriptor }) => !!hash && !!name && !!descriptor)
      .forEach(({ hash, name, descriptor }: ImporterFolderInfoType) => {
        contents.push({
          id: hash,
          downloadKey: descriptor,
          name,
          isFile: false,
          sizeDisplay: '',
          dateDisplay: '',
          parentHash: `${parent.parentHash || parentHash}/${hash}`,
        });
      });

    return {
      cursor,
      files: contents,
      hasMore: !!cursor,
    };
  } catch (error) {
    if (matchesAirror(error, INVALID_IMPORT_SESSION)) {
      throw error;
    }

    reportErrorToBugsnag({
      error,
      context: 'Can not fetch files',
      metadata: {
        data: {
          source,
          descriptor: path,
        },
      },
    });

    return {
      files: [],
      cursor: '',
      hasMore: false,
    };
  }
};

type ImporterTaskFallbackBoard = Pick<Board, 'id' | 'title' | 'thumbnails'>;

export const IMPORTER_TASK_FALLBACK_BOARDS: Record<'WORKSPACE_ROOT' | 'DELETED_BOARD', ImporterTaskFallbackBoard> = {
  WORKSPACE_ROOT: {
    id: '',
    title: 'All media',
    thumbnails: [],
  },
  DELETED_BOARD: {
    id: '',
    title: 'Board no longer exists',
    thumbnails: [],
  },
};

export const enhanceImporterTask = (
  task: Awaited<ReturnType<typeof Tasks.listTasks<string, 'Importer'>>>['items'][number],
  boardsMap: Map<string, Pick<Board, 'id' | 'title' | 'thumbnails'>>,
): ImporterTaskEnhanced => {
  return {
    ...task,
    metadata: {
      ...task.args,
      board: task.args.parentBoardId
        ? (boardsMap.get(task.args.parentBoardId) ?? IMPORTER_TASK_FALLBACK_BOARDS.DELETED_BOARD)
        : IMPORTER_TASK_FALLBACK_BOARDS.WORKSPACE_ROOT,
      numRootFiles: task.args.files?.length ?? 0,
      numRootFolders: task.args.folders?.length ?? 0,
    },
  };
};
